import {StatusCodes} from 'http-status-codes';
import React from 'react';
import {useDispatch} from 'react-redux';
import {
  SOURCE_AMBIENT_WEATHER,
  SOURCE_DAVIS,
  SOURCE_ECOWITT,
  SOURCE_NETATMO_WEATHER,
  SOURCE_WEATHERFLOW,
  SOURCE_WUNDERGROUND,
  WeatherService,
} from '../api';
import {StationSource, WeatherStationFromDetail} from '../api/types';
import {addStation, removeStation, setAlert, setStationDetail} from '../store';
import {Dates} from '../utils';
import useApiClient, {ErrorInfo} from './useApiClient';

const useFetchStationDetail = () => {
  const {fetch, loading} = useApiClient();
  const dispatch = useDispatch();

  const fetchStationDetail = React.useCallback(
    (stationId: number, callback?: () => void, errorCallback?: () => void) => {
      fetch(WeatherService.stationDetail(stationId), {ignore404: true})
        .then((data: WeatherStationFromDetail) => {
          dispatch(addStation(data));
          dispatch(setStationDetail(data));
          callback && callback();
        })
        .catch((err: ErrorInfo) => {
          if (err.statusCode === StatusCodes.NOT_FOUND) {
            dispatch(removeStation(stationId));
            dispatch(
              setAlert({
                message: 'This station is not available anymore.',
                type: 'ERROR',
              }),
            );
          }
          errorCallback && errorCallback();
        });
    },
    [dispatch, fetch],
  );

  /**
   * Tells whether station requires to be updated
   *
   * It currently depends on the source and last time it has been fetched
   */
  const stationUpdateRequired = React.useCallback(
    (
      lastDataFetch: number,
      lastUpdate: number,
      source: StationSource,
      linked: boolean,
    ) => {
      const lastUpdateDelay = Dates.timeDiffUtcWithNowInSeconds(lastUpdate);
      const lastFetched = Dates.timeDiffUtcWithNowInSeconds(lastDataFetch);
      let delay = 120;
      if (linked) {
        if (
          [
            SOURCE_AMBIENT_WEATHER,
            SOURCE_WUNDERGROUND,
            SOURCE_WEATHERFLOW,
            SOURCE_ECOWITT,
            SOURCE_DAVIS,
          ].includes(source)
        ) {
          delay = 70;
        } else if (source === SOURCE_NETATMO_WEATHER) {
          delay = 600;
        }
      }
      if (lastUpdateDelay >= delay && lastFetched > 20) {
        // Last Station detail is outdated, update required
        return true;
      }
      return false;
    },
    [],
  );

  return React.useMemo(
    () => ({
      fetchStationDetail,
      stationUpdateRequired,
      loading,
    }),
    [fetchStationDetail, loading, stationUpdateRequired],
  );
};

export default useFetchStationDetail;
